import React, { useCallback, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Checkbox,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  Link,
} from "@mui/material";
import { IAnimal } from "../../modules/animals/interface";
import image from "../../assets/jpg/no_image.jpg";
import { useTranslate } from "../../hooks/useTranslate";
import { Delete, Edit, Visibility } from "@mui/icons-material";
import { useDialog } from "../../hooks/useDialog";
import { amber } from "@mui/material/colors";
import { Link as RouterLink } from "react-router-dom";

interface IAnimalTab {
  animals: IAnimal[];
  onDeleted: (ids: string[]) => void;
}

interface IAnimalItem {
  animal: IAnimal;
  checked: boolean;
  onCheckChange: (id: string, checked: boolean) => void;
}

export const AnimalItem: React.FC<IAnimalItem> = ({
  animal,
  checked,
  onCheckChange,
}) => {
  const { translate } = useTranslate();
  return (
    <Paper
      key={animal.id}
      sx={{
        p: 1,
        bgcolor: !animal.batch && !animal.flock ? amber[50] : "grey.50",
      }}
      elevation={0}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid item md={false}>
          <Avatar src={animal?.photo ?? image} alt={animal.name} />
        </Grid>
        <Grid item md>
          <Stack
            component={Link}
            underline="none"
            href={`/animals/${animal.id}`}
          >
            <Typography>
              #{animal.number_identifier} <strong>{animal.name}</strong>
            </Typography>
            <Typography variant="caption">
              {`${translate(`animals.${animal.gender}`)} / ${translate(
                "animals.placeholder_batch"
              )}: ${
                animal.batch?.name ?? translate("global.uninformed")
              } / ${translate("animals.placeholder_flock")}: ${
                animal.flock?.name ?? translate("global.uninformed")
              }`}
            </Typography>
          </Stack>
        </Grid>
        <Grid item md={false}>
          <Stack direction="row" spacing={1}>
            <IconButton
              component={RouterLink}
              to={`./${animal.id}`}
              size="small"
            >
              <Visibility />
            </IconButton>
            <IconButton
              component={RouterLink}
              to={`./edit/${animal.id}`}
              size="small"
            >
              <Edit />
            </IconButton>
          </Stack>
        </Grid>
        <Grid item md={false}>
          <Checkbox
            checked={checked}
            onChange={(_, checked) => {
              onCheckChange(animal.id, checked);
            }}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

const AnimalTab: React.FC<IAnimalTab> = ({ animals, onDeleted }) => {
  const dialog = useDialog();
  const { translate } = useTranslate();
  const [selectedAnimals, setSelectedAnimals] = useState<string[]>([]);

  const isSelectedAllAnimals = useMemo(() => {
    return animals.reduce((is, animal) => {
      if (is && !selectedAnimals.includes(animal.id)) return false;
      return is;
    }, true);
  }, [animals, selectedAnimals]);

  const selectAllAnimals = useCallback(() => {
    setSelectedAnimals(animals.map((o) => o.id));
  }, [animals]);

  const unselectAllAnimals = useCallback(() => {
    setSelectedAnimals([]);
  }, []);

  return (
    <Stack spacing={2} m={2}>
      <Stack
        sx={{ alignSelf: "flex-end", position: "sticky" }}
        alignItems="center"
        justifyContent="center"
        direction="row"
        spacing={1}
        mx={1.5}
      >
        <Button
          sx={{ textTransform: "lowercase" }}
          onClick={
            !isSelectedAllAnimals ? selectAllAnimals : unselectAllAnimals
          }
        >
          {translate(
            !isSelectedAllAnimals ? "global.select_all" : "global.unselect_all"
          )}
        </Button>

        <Collapse
          unmountOnExit
          orientation="horizontal"
          in={selectedAnimals.length > 0}
        >
          <IconButton
            color="primary"
            size="small"
            onClick={() => {
              dialog.open({
                title: translate(
                  !isSelectedAllAnimals
                    ? "management.title_modal_confirm_selected_animal"
                    : "management.title_modal_confirm_animal"
                ),
                message: translate(
                  !isSelectedAllAnimals
                    ? "management.subtitle_modal_confirm_selected_animal"
                    : "management.subtitle_modal_confirm_animal"
                ),
                buttons: [
                  {
                    label: translate("global.cancel"),
                    options: {
                      color: "inherit",
                    },
                  },
                  {
                    label: translate("global.delete"),
                    async callback() {
                      onDeleted(selectedAnimals);
                    },
                    options: {
                      color: "error",
                      sx: { fontWeight: "bold" },
                    },
                  },
                ],
              });
            }}
          >
            <Delete />
          </IconButton>
        </Collapse>
      </Stack>
      {animals.map((animal) => (
        <AnimalItem
          key={animal.id}
          animal={animal}
          checked={selectedAnimals.includes(animal.id)}
          onCheckChange={(animalId, checked) => {
            setSelectedAnimals((old) => {
              if (checked) {
                return [...old.filter((o) => o !== animal.id), animal.id];
              } else {
                return [...old.filter((o) => o !== animal.id)];
              }
            });
          }}
        />
      ))}
    </Stack>
  );
};

export default AnimalTab;
